import { useDispatch, useSelector, type TypedUseSelectorHook } from "react-redux";
import type { BrowserType } from "components/types/browser";
import type {
  Address,
  MembershipBag,
  MembershipState,
  MembershipItem,
  NotifyMeItem,
  Product,
  ShippingCutoff,
  WatchlistNotificationRule,
} from "@rtr/godmother";
import type { WorkingFilters } from "components/types/working-filters";
import type { DiscoProduct } from "components/types/disco-product";
import type { FilterOptions } from "components/types/filter-options";
import type { UserData } from "components/types/user-data";
import type SwapConfirmation from "@rtr/godmother/dist/types/SwapConfirmation";
import type { DateOptions } from "components/types/date-options";
import type { DisplayedModal } from "components/types/displayed-modal";
import type { FlagsAndExperiments } from "components/types/flags-and-experiments";
import type { GridView } from "components/types/grid-view";
import type { SmartComponentState } from "components/types/pdp-content-smart-component";
import type { SMBag } from "components/types/sm-bag";
import type { HomePickup } from "components/types/home-pickup";
import type { GridMetadata } from "components/types/grid-metadata";
import type { CMSProductCarousel } from "components/types/cms-product-carousel";
import type { ContentModule } from "components/types/content-modules";
import type { FeatureFlags } from "components/types/flags";
import type { StorefrontShortlist } from "components/types/shortlists";
import type { UserProblems } from "components/types/user-problems";
import type { PDP } from "components/types/pdp";
import type { CommunityProfile } from "components/types/community-profile";
import type { WorkingSort } from "components/types/working-sorts";
import type { InformedFilters } from "components/types/informed-filters";
import type { VisualNav } from "components/types/visual-nav";
import type { AvailabilityScopeType } from "components/types/disco-search-type";
import type { NextGridTypeType } from "components/types/next-grid-types";

// named store-types.ts to avoid confusion with the store.js file
// when we migrate our redux store to typescript, we can use this file and rename it store.ts

export interface RootState {
  accessoryOnly?: boolean;
  addresses: Address[];
  availabilityScope: AvailabilityScopeType;
  browser: BrowserType;
  cameFromManageNotificationsView: boolean;
  ceBagCount?: number;
  ceBagLoading?: boolean;
  cmsBodyComponents: CMSProductCarousel[]; // for now, this will only be carousels but can be extended to other CMS components in the future
  communityProfile: CommunityProfile;
  contentModules: ContentModule[];
  currentPage?: number;
  dateOptions: DateOptions;
  deliveryCutoffs: ShippingCutoff[];
  displayedModal: DisplayedModal;
  enabledFlags: FeatureFlags;
  filterOptions: FilterOptions;
  flagsAndExperiments: FlagsAndExperiments;
  gridCount?: number;
  gridFilterModalOpen?: boolean;
  gridMetaData: GridMetadata;
  gridMigratedToNext: boolean;
  gridProducts?: DiscoProduct[];
  gridType?: string;
  gridView: GridView;
  homePickup: HomePickup;
  isAddressBookLoading?: boolean;
  isBuyNow?: boolean;
  isSwapsGrid?: boolean;
  informedFilters: InformedFilters;
  itemLimit?: number;
  loading?: boolean;
  membershipBag: MembershipBag;
  membershipItems?: MembershipItem[];
  membershipRentalBeginDate: string;
  membershipState: MembershipState;
  membershipStateLoading?: boolean;
  migratedToNext: boolean;
  nextGridType: NextGridTypeType;
  notificationRules: WatchlistNotificationRule[];
  notifyMeFetched: boolean;
  notifyMeLoading: boolean;
  notifyMeProducts: NotifyMeItem[];
  pageType: string;
  path: string;
  pdp: PDP;
  pdpSelectedSize: string;
  predictedZip: string;
  product: Product;
  proratedPrice?: {
    addonPrice: string;
  };
  shortlistId?: number;
  shortlists: StorefrontShortlist[];
  shouldRefetchMembershipCarousels?: boolean;
  smartComponentState?: SmartComponentState;
  smBag?: SMBag;
  staticProduct?: DiscoProduct;
  swapOrderConfirmation: SwapConfirmation;
  url?: string;
  userData: Partial<UserData>;
  userProblems: UserProblems;
  visualNav?: VisualNav;
  workingFilters: WorkingFilters;
  workingSort: WorkingSort;
}

// Use instead of `useDispatch` and `useSelector` to avoid needing to type check them in every component
export const useAppDispatch: () => ReturnType<typeof useDispatch> = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// TODO: add useStore type when we upgrade redux and migrate util/store to typescript
